<template>
  <ElDialog
    :title="$t('APP.RELEASES.EDIT')"
    :visible="value"
    :close-on-click-modal="false"
    width="35%"
    class="edit-release-modal"
    @close="closeModal"
  >
    <InputWithLabel
      :label="$t('APP.RELEASES.CHANGELOGS_FA')"
      class="edit-release-modal__changelog"
    >
      <Editor v-model="releaseModel.changelogFa" dir="rtl" />
    </InputWithLabel>

    <InputWithLabel
      :label="$t('APP.RELEASES.CHANGELOGS_EN')"
      class="edit-release-modal__changelog"
    >
      <Editor v-model="releaseModel.changelogEn" dir="ltr" class="text-latin" />
    </InputWithLabel>

    <ElSwitch
      v-model="releaseModel.autoPublish"
      :disabled="!isWaitingState"
      class="edit-release-modal__auto-publish"
      :active-text="$t('APP.RELEASES.AUTO_PUBLISH')"
    />

    <div
      v-if="app.alphaEnabled || app.betaEnabled"
      class="edit-release-modal__auto-publish"
    >
      <header class="edit-release-modal__heading">
        {{ $t('APP.DRAFT.STEPS.FINAL_CHECK.PUBLISHING_GROUP') }}
      </header>

      <ElRadioGroup
        v-model="publishGroup"
        class="edit-release-modal__auto-publish"
        @change="updateGroupPublish"
      >
        <ElRadio
          v-if="app.alphaEnabled"
          :label="'alpha'"
          :disabled="
            !app.alphaEnabled ||
              currentPublishGroup === 'public' ||
              currentPublishGroup === 'beta'
          "
        >
          {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.ALPHA') }}
          <span v-if="!app.alphaEnabled">(غیر فعال)</span>
        </ElRadio>

        <ElRadio
          v-if="app.betaEnabled"
          :label="'beta'"
          :disabled="!app.betaEnabled || currentPublishGroup === 'public'"
        >
          {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.BETA') }}
          <span v-if="!app.betaEnabled">(غیر فعال)</span>
        </ElRadio>

        <ElRadio :label="'public'">
          {{ $t('APPS.DRAFT.STEPS.FINAL_CHECK.PUBLIC') }}
        </ElRadio>
      </ElRadioGroup>
    </div>

    <StagedRollout
      v-if="canUseStagedRollout"
      class="edit-release-modal__staged-rollout"
      :show-disabled-message="false"
      :disabled="!canUseStagedRollout"
      :expanded="expandStagedRollout"
      :can-decrease="isPublicWaitingState"
      :initial-percentage="release.stagedRolloutPercentage"
      :percentage.sync="releaseModel.stagedRolloutPercentage"
      :start="stagedRollout.start"
      :step="stagedRollout.step"
    />

    <div>
      <ElSelect
        v-if="app.alphaEnabled && publishGroup === 'alpha'"
        v-model="selectedRelease"
        class="new-release__select"
        filterable
        :placeholder="$t('PICK_A_ALPHA_GROUP')"
        @change="updateAlphaReleaseGroups"
      >
        <ElOption
          v-for="releaseItem in alphaGroup"
          :key="releaseItem.id"
          :value="releaseItem.id"
          :label="`${releaseItem.name}`"
        />
      </ElSelect>
    </div>

    <AddAlphaGroupModal
      v-model="showAddAlphaGroupModal"
      :package-name="packageName"
    />

    <div slot="footer" class="edit-release-modal__footer">
      <div>
        <ElButton
          type="primary"
          size="medium"
          :loading="loading.updateRelease"
          @click="updateRelease"
        >
          {{ $t('SAVE') }}
        </ElButton>
        <ElButton type="text" size="medium" :plain="true" @click="closeModal">
          {{ $t('CANCEL') }}
        </ElButton>
      </div>

      <ElButton
        v-if="release.actions.deactivate"
        class="edit-release-modal__deactivate"
        type="text"
        plain
        size="medium"
        :loading="loading.deactivate"
        @click="confirmDeactivateRelease"
      >
        {{ $t('APP.RELEASES.DEACTIVATE_RELEASE') }}
      </ElButton>
    </div>
  </ElDialog>
</template>

<script>
import {
  UPDATE_RELEASE,
  REPLACE_RELEASE,
  APP_DETAILS,
  ALPHA_GROUP,
  BETA_GROUP,
} from '@/store/types';
import packageName from '@/mixins/packageName';
import Editor from '@@/common/Editor';
import InputWithLabel from '@@/common/InputWithLabel';
import Release from '@/api/app-releases';
import StagedRollout from '@@/app/AppReleases/NewRelease/StagedRollout';
import { mapActions, mapGetters } from 'vuex';
import AddAlphaGroupModal from '../../Group/AddAlphaGroupModal.vue';

export default {
  name: 'EditReleaseModal',
  components: {
    Editor,
    InputWithLabel,
    StagedRollout,
    AddAlphaGroupModal,
  },
  mixins: [packageName],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    release: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: {
        updateRelease: false,
        deactivate: false,
      },
      publishGroup: '',
      showAddAlphaGroupModal: false,
      selectedRelease: null,
      releaseModel: this.getReleaseModel(),
      currentPublishGroup: null,
    };
  },
  computed: {
    ...mapGetters({
      app: APP_DETAILS,
      alphaGroup: ALPHA_GROUP,
      betaGroup: BETA_GROUP,
    }),
    canUseStagedRollout() {
      return this.app.canStageRollOut;
    },
    isWaitingState() {
      return this.release.publishState === 'waiting';
    },
    isPublicWaitingState() {
      if (
        !this.release?.releaseGroups ||
        !this.release?.releaseGroups?.length
      ) {
        return this.release.publishState === 'waiting';
      }

      return this.release.publishState;
    },
    expandStagedRollout() {
      return (
        this.canUseStagedRollout &&
        this.releaseModel.stagedRolloutPercentage < 100
      );
    },
    stagedRollout() {
      return this.release?.stagedRollout || {};
    },
  },
  watch: {
    release() {
      this.releaseModel = this.getReleaseModel();
    },
    value(newValue) {
      if (newValue && this.release?.releaseGroups?.length > 0) {
        this.release.releaseGroups.some(group => {
          if (group.enabled) {
            if (group.name !== 'beta') {
              this.currentPublishGroup = 'alpha';
              this.publishGroup = 'alpha';
              this.selectedRelease = group.id;
              return true;
            } else {
              this.currentPublishGroup = 'beta';
              this.publishGroup = 'beta';
              this.releaseModel.releaseGroups = [group.id];
              return true;
            }
          }
          return false;
        });
      } else {
        this.currentPublishGroup = 'public';
        this.publishGroup = 'public';
      }
    },
  },
  methods: {
    ...mapActions({
      dispatchUpdateRelease: UPDATE_RELEASE,
      replaceRelease: REPLACE_RELEASE,
      updateRelease: UPDATE_RELEASE,
    }),
    closeModal() {
      this.$emit('input', false);
    },
    getReleaseModel() {
      const {
        changelogEn,
        changelogFa,
        autoPublish,
        stagedRolloutPercentage,
        releaseGroups,
      } = this.release;
      return {
        changelogEn,
        changelogFa,
        autoPublish,
        stagedRolloutPercentage,
        releaseGroups,
      };
    },
    updateRelease() {
      this.loading.updateRelease = true;
      const queryParams = {
        token: this.release.token,
        packageName: this.packageName,
      };

      let updateBody = this.releaseModel;
      if (!updateBody?.releaseGroups) {
        updateBody.releaseGroups = [];
      }
      if (
        updateBody?.releaseGroups?.length > 0 &&
        updateBody?.releaseGroups[0]?.id
      ) {
        updateBody.releaseGroups = [updateBody?.releaseGroups[0]?.id];
      }
      return this.dispatchUpdateRelease({
        queryParams,
        updateBody,
      })
        .then(message => {
          if (message) {
            this.$message.success(message);
          }
          this.closeModal();
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.loading.updateRelease = false;
        });
    },
    confirmDeactivateRelease() {
      return this.$confirm(
        this.$t('APP.RELEASES.DEACTIVE_WARNING'),
        this.$t('WARNING'),
        {
          confirmButtonText: this.$t('DEACTIVATE'),
          cancelButtonText: this.$t('CANCEL'),
          confirmButtonClass: 'edit-release-modal__delete-confirm-button',
          type: 'error',
        },
      )
        .then(() => {
          this.deactivateRelease();
        })
        .catch(() => {});
    },
    deactivateRelease() {
      this.loading.deactivate = true;
      return Release.deactivateRelease({
        releaseId: this.release.token,
        packageName: this.packageName,
      })
        .then(({ release, message }) => {
          this.replaceRelease(release);
          if (message) {
            this.$message.success(message);
          }
          this.closeModal();
        })
        .catch(({ message }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.loading.deactivate = false;
        });
    },
    updateGroupPublish(value) {
      this.publishGroup = value;
      if (value === 'public') {
        this.releaseModel.releaseGroups = [];
      } else if (value === 'beta') {
        this.releaseModel.releaseGroups = [this.betaGroup?.[0]?.id];
      }
    },
    updateAlphaReleaseGroups(value) {
      this.releaseModel.releaseGroups = [value];
    },
  },
};
</script>

<style lang="scss">
.edit-release-modal {
  &__deactivate {
    color: $--color-danger;
    border: 0;
    font-size: $--font-size-base * 0.95;

    &:hover,
    &:focus {
      color: $--color-danger !important;
    }
  }

  &__changelog {
    margin-bottom: $global-margin;
  }

  &__delete-confirm-button {
    background-color: $alizarin;
    border: 0;

    &:focus {
      background-color: $alizarin;
    }
  }

  &__staged-rollout {
    margin-bottom: $global-margin * 2;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .el-switch__label {
    margin-right: calc($global-margin / 2);

    * {
      font-size: 0.8rem;
    }
  }
  &__auto-publish {
    margin-left: auto;
    margin-top: $global-margin;
    @include rtl() {
      margin-left: 0;
      margin-right: auto;
    }
  }
  &__header {
    display: flex;
    align-items: center;
    margin-bottom: $global-margin * 2;
  }
}
</style>
